import CheckboxInput from '@/components/form-inputs/CheckboxInput';
import '@/components/program/travel-customization.styles.css';
import { TextBodyExtraExtraSmall } from '@/components/text/Texts';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import { TRAVEL_DAYS_OF_THE_WEEK } from '@/constants/travel-program';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import type { DialogProps } from 'primereact/dialog';
import { classNames } from 'primereact/utils';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { MdArrowBack } from 'react-icons/md';
import DayButton from './DayButton';
import SecondaryModal from './SecondaryModal';
import { InputController } from '../inputs/InputController';
import DropdownInput from '../inputs/DropdownInput';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddCustomTimeModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;
	const {
		control,
		getValues,
		resetField,
		setValue,
		trigger,
		watch,
		formState: { errors },
	} = useFormContext();

	const isAllDayValue = watch('isAllDay');
	const daysValue = watch('days');

	const generateTimeSlots = () => {
		const slots = [];
		let hour = 0;
		let minute = 0;

		while (hour < 24) {
			const ampm = hour < 12 ? 'am' : 'pm';
			const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
			const formattedMinute = minute === 0 ? '00' : '30';

			const label =
				formattedHour < 10
					? `0${formattedHour}:${formattedMinute} ${ampm}`
					: `${formattedHour}:${formattedMinute} ${ampm}`;
			const value = `${formattedHour}:${formattedMinute} ${ampm}`;

			slots.push({ label: label, value: value });

			// Increment time by 30 minutes
			minute += 30;
			if (minute === 60) {
				minute = 0;
				hour++;
			}
		}
		return slots;
	};

	const timeSlots = generateTimeSlots();

	const handleOnCancel = useCallback(() => {
		setValue('days', []);
		resetField('startTime');
		resetField('endTime');
		resetField('isAllDay');
		setVisible(false);
	}, [resetField, setValue, setVisible]);

	const handleOnSave = useCallback(() => {
		trigger(['days', 'startTime', 'endTime']).then(() => {
			if (!errors.days && !errors.startTime && !errors.endTime) {
				setVisible(false);
			}
		});
	}, [errors.days, errors.endTime, errors.startTime, setVisible, trigger]);

	return (
		<SecondaryModal
			{...rest}
			className="w-rem-40"
			draggable={false}
			header={
				<HeaderContent
					label="Add custom time"
					icon={
						<MdArrowBack
							size={30}
							className="text-primary"
							onClick={() => setVisible(false)}
						/>
					}
				/>
			}
			footer={
				<FooterContent
					formName={TRAVEL_PROGRAM_FORM}
					id="custom-time"
					onClickCancel={handleOnCancel}
					onClickSave={handleOnSave}
				/>
			}
		>
			<div className="font-poppins">
				<div>
					<span className="text-lg">Select Day(s)</span>
					<div className="space-x-3 mt-3">
						{TRAVEL_DAYS_OF_THE_WEEK.map((day) => (
							<DayButton
								key={day.label}
								onLabel={day.label}
								offLabel={day.label}
								checked={daysValue.includes(day.value)}
								onChange={() => {
									if (getValues('days').includes(day.value)) {
										const filteredDays = getValues(
											'days',
										).filter(
											(item: string) =>
												item !== day.value,
										);
										setValue('days', filteredDays);
									} else {
										setValue('days', [
											...getValues('days'),
											day.value,
										]);
									}
								}}
							/>
						))}
						{errors.days && (
							<TextBodyExtraExtraSmall
								id={'days-helper'}
								className="block mt-1 text-error"
								text="Please select at least one(1) day"
							/>
						)}
					</div>
					<div className="space-x-6 mt-8 flex">
						{/* TODO not sure about right styles. didnt find this control on figma design */}
						<InputController id="startTime" control={control}>
							<DropdownInput
								disabled={isAllDayValue}
								options={timeSlots}
								label="Start Time"
								placeholder="Start Time"
							/>
						</InputController>
						{/* <DropdownInput
							className="w-full min-w-52 default-dropdown"
							collapseIcon={<i />}
							control={control}
							disabled={isAllDayValue}
							dropdownIcon={<i />}
							formName={TRAVEL_PROGRAM_FORM}
							id="startTime"
							label="Start Time"
							options={timeSlots}
							placeholder="Start Time"
							pt={{
								root: { className: 'tc-dropdown' },
								input: { className: 'tc-dropdown-content' },
								item: { className: 'tc-dropdown-content' },
							}}
						/> */}

						{/* TODO not sure about right styles. didnt find this control on figma design */}
						<InputController id="endTime" control={control}>
							<DropdownInput
								disabled={isAllDayValue}
								options={timeSlots}
								label="End Time"
								placeholder="End Time"
							/>
						</InputController>
						{/* <DropdownInput
							className="w-full min-w-52 default-dropdown"
							collapseIcon={<i />}
							control={control}
							disabled={isAllDayValue}
							dropdownIcon={<i />}
							formName={TRAVEL_PROGRAM_FORM}
							id="endTime"
							label="End Time"
							options={timeSlots}
							placeholder="End Time"
							pt={{
								root: { className: 'tc-dropdown' },
								input: { className: 'tc-dropdown-content' },
								item: { className: 'tc-dropdown-content' },
							}}
						/> */}
					</div>
					<div className="flex items-center mt-3">
						<CheckboxInput
							control={control}
							formName={TRAVEL_PROGRAM_FORM}
							id="isAllDay"
							pt={{
								root: {
									className: 'hover:bg-transparent',
								},
								box: {
									className: classNames('tc-checkbox-input', {
										'tc-checkbox-input-checked':
											isAllDayValue,
									}),
								},
								icon: {
									className: 'tc-checkbox-icon',
								},
							}}
							text="All day (24 hours)"
						/>
					</div>
				</div>
			</div>
		</SecondaryModal>
	);
};

export default AddCustomTimeModal;
